// The "Invite someone" form.
//
// Rendered on TWO tabs, which is the whole reason it is its own module: Manage
// is where you configure a group, but Members is where you go when you are
// thinking about people, and that is where it was looked for first. It used to
// live inside groupManage.ts, and leaving it there while adding a second caller
// is how the message-copy block ended up duplicated earlier the same day.
//
// Who sees it is NOT decided here. The caller asks `canInvite(myRole,
// group.invitePermission)` -- the same function the server gates on -- so the
// form is never rendered to someone who would then be refused.

import { ApiClientError, getMyInviteLink, inviteMember } from '../api/client.js';
import { el } from '../dom.js';
import { roleSelect } from './roleSelect.js';
import {
  GroupRole,
  maxInvitableRole,
  type GroupDetail,
  type InviteMemberResponse,
} from '@msc/shared';

/** Distinguishes two instances of this form on one page. */
let idSeq = 0;

export interface InviteSectionOptions {
  /**
   * Called after a successful invite, so the caller can refresh whatever it
   * shows that just changed — the roster and the member count.
   *
   * This is the SPA's answer to PHP's post-redirect-get. There is no page load
   * to redirect to, so nothing re-reads the database on its own: the form
   * reported success while the roster above it kept rendering the list fetched
   * at page load, which read as the add having silently failed. (It had not —
   * the row was there.)
   *
   * It deliberately does NOT re-render the whole view. A full re-render would
   * destroy this form and take the success message with it, which is the exact
   * thing PHP's session flash exists to carry across a redirect. Refreshing
   * only the changed parts keeps the message, the scroll position and focus.
   *
   * Errors are the caller's to handle; a refresh that throws must not make a
   * successful invite look failed.
   */
  onInvited?: () => void | Promise<void>;
}

export function inviteSection(
  group: GroupDetail,
  opts: InviteSectionOptions = {},
): HTMLElement {
  // Both tabs render this form, never at the same time today -- but ids that
  // could collide are not worth the bet, and a `for=` attribute silently
  // attaches to the FIRST match, which would make one form's labels drive the
  // other's fields.
  const uid = `invite-${(idSeq += 1)}`;

  const error = el('p', { class: 'form-error', hidden: true });
  const ok = el('p', { class: 'form-ok', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Send invite');

  const email = el('input', {
    id: `${uid}-email`,
    name: 'email',
    type: 'email',
    required: true,
    placeholder: 'them@example.com',
  }) as HTMLInputElement;

  // Text invites reach EXISTING members only — someone who already has an
  // account and verified this very number. Inviting a brand-new person by text
  // 400s at the server (see groups/routes.ts Path B): they never opted in, so
  // the message would be an undeclared A2P opt-in path. The hint below sets
  // that expectation before the organizer types a number and gets refused.
  const phone = el('input', {
    id: `${uid}-phone`,
    name: 'phone',
    type: 'tel',
    inputmode: 'tel',
    placeholder: '555 123 4567',
  }) as HTMLInputElement;

  const byEmail = el('input', {
    type: 'radio',
    name: `${uid}-method`,
    id: `${uid}-by-email`,
    value: 'email',
  }) as HTMLInputElement;
  byEmail.checked = true;
  const bySms = el('input', {
    type: 'radio',
    name: `${uid}-method`,
    id: `${uid}-by-sms`,
    value: 'sms',
  }) as HTMLInputElement;

  const emailRow = el('div', { class: 'field' }, [
    el('label', { for: `${uid}-email` }, 'Email address'),
    email,
  ]);
  const phoneRow = el('div', { class: 'field' }, [
    el('label', { for: `${uid}-phone` }, 'Mobile number'),
    phone,
    el(
      'p',
      { class: 'muted small' },
      'If this number already belongs to a member, they are added right away. ' +
        'Otherwise you get a link to text them from your own phone.',
    ),
  ]);

  /**
   * Move `required` to whichever field is visible.
   *
   * This matters more here than on the register form, because this form
   * deliberately has NO `novalidate` — see the note below. A `required` input
   * that is hidden makes the form permanently invalid, and the browser refuses
   * the submit while having nowhere to show the message. Silent dead button.
   */
  function renderMethod(): void {
    const sms = bySms.checked;
    emailRow.hidden = sms;
    phoneRow.hidden = !sms;
    email.required = !sms;
    phone.required = sms;
  }
  byEmail.addEventListener('change', renderMethod);
  bySms.addEventListener('change', renderMethod);
  renderMethod();

  // A non-admin can only ever add a plain Member, so there is no choice to
  // offer -- a select with one option is a control that looks live and is not.
  const maxRole = maxInvitableRole(group.myRole);
  const canPickRole = maxRole > GroupRole.MEMBER;
  const role = roleSelect(`${uid}-role`, maxRole);
  const roleRows: HTMLElement[] = canPickRole
    ? [
        el('label', { for: `${uid}-role` }, 'Join as'),
        role,
        el(
          'p',
          { class: 'muted small' },
          'They land in this role as soon as they accept. There is no second promotion step.',
        ),
      ]
    : [
        el(
          'p',
          { class: 'muted small' },
          'They join as a Member. Only an admin can invite someone into a higher role.',
        ),
      ];

  // No `novalidate`: this form's `required` and `type="email"` were inert behind
  // it, so a malformed address round-tripped to the server for its error.
  const form = el('form', { method: 'post', action: `/groups/${group.groupId}/invites` }, [
    el('fieldset', { class: 'method-choice' }, [
      el('legend', {}, 'Invite by'),
      el('label', { class: 'check', for: `${uid}-by-email` }, [byEmail, ' Email']),
      el('label', { class: 'check', for: `${uid}-by-sms` }, [bySms, ' Text message']),
    ]),
    emailRow,
    phoneRow,
    ...roleRows,
    error,
    ok,
    submit,
  ]) as HTMLFormElement;

  // Filled after a phone invite to someone NEW. Outside the form on purpose: its
  // buttons are not part of the submit, and it must survive the form clearing.
  const linkPanel = el('div', { class: 'invite-link-panel', hidden: true });

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    ok.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      const sms = bySms.checked;
      const result = await inviteMember(group.groupId, {
        method: sms ? 'sms' : 'email',
        // Only the chosen channel's field, so a stale value in the hidden one
        // cannot reach the server alongside it.
        ...(sms ? { phone: phone.value.trim() } : { email: email.value.trim() }),
        role: Number(role.value),
      });
      if (result.mode === 'link' && result.invite) {
        showLinkPanel(linkPanel, result.invite);
      } else {
        linkPanel.hidden = true;
        ok.textContent = successMessage(result);
        ok.hidden = false;
      }
      email.value = '';
      phone.value = '';
      // After the message, not before: if the refresh throws, the invite still
      // succeeded and the inviter must still be told so.
      try {
        await opts.onInvited?.();
      } catch {
        // The add stands regardless. Leaving the stale roster on screen is a
        // smaller lie than reporting a failure that did not happen, and the
        // next navigation fixes it.
      }
      // ⚠ STAYS DISABLED after a success, until a field actually changes.
      //
      // The fields are cleared just above, so a second press should already be
      // refused by `required` -- but that depends on the browser not having
      // re-filled them, and autofill does exactly that. This is an OUTBOUND
      // SEND triggered by a button whose own success notice renders above it
      // and shoves it down the page under the cursor, so "probably refused" is
      // not the standard to hold it to. Latching it off makes a second mail
      // impossible rather than unlikely.
      //
      // Re-armed by `rearm` below on any input, so inviting two people in a
      // row costs nothing.
      submit.setAttribute('disabled', 'disabled');
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
      // Only on FAILURE. Nothing was sent, so the same press should work.
      submit.removeAttribute('disabled');
    }
  });

  // Any edit means a different invite is being composed, so the button comes
  // back. `input` rather than `change`: `change` on a text field waits for
  // blur, which would leave the button dead while somebody is typing into it.
  const rearm = (): void => submit.removeAttribute('disabled');
  email.addEventListener('input', rearm);
  phone.addEventListener('input', rearm);
  role.addEventListener('change', rearm);

  return el('section', { class: 'card' }, [
    el('h2', {}, 'Invite someone'),
    personalLinkBlock(group),
    form,
    linkPanel,
  ]);
}

/**
 * "Paste this into your group text": the member's OWN reusable link.
 *
 * Fetched on press and not on render, because the first press mints the code and
 * a page load must never write. The same button hands back the same link every
 * time. Rendered only where this whole section is (the caller's `canInvite`
 * gate), and the server enforces the same rule, so a member of an admins-only
 * group sees neither the form nor this.
 */
function personalLinkBlock(group: GroupDetail): HTMLElement {
  const status = el('p', { class: 'muted small', role: 'status' }, '');
  const out = el('div', { class: 'invite-link-panel', hidden: true });
  const get = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Get my invite link');

  get.addEventListener('click', async () => {
    status.textContent = '';
    get.setAttribute('disabled', 'disabled');
    try {
      const { url } = await getMyInviteLink(group.groupId);
      const message = `Join ${group.name} on MySocialCal: ${url}`;
      const field = el('input', {
        type: 'text',
        class: 'invite-link-url',
        readonly: true,
        value: url,
        'aria-label': 'Your invite link',
      }) as HTMLInputElement;
      field.addEventListener('focus', () => field.select());

      const copy = el('button', { type: 'button', class: 'btn' }, 'Copy link');
      copy.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(message);
          status.textContent = 'Copied. Paste it into your group text.';
        } catch {
          field.select();
          status.textContent = 'Could not copy. The link is selected: copy it yourself.';
        }
      });

      const actions: HTMLElement[] = [copy];
      if (typeof navigator.share === 'function') {
        const share = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Share');
        share.addEventListener('click', () => {
          // A dismissed share sheet rejects; that is the user changing their mind.
          void navigator.share({ text: message }).catch(() => undefined);
        });
        actions.push(share);
      }

      out.replaceChildren(
        field,
        el('div', { class: 'invite-link-actions' }, actions),
        el(
          'p',
          { class: 'muted small' },
          'Anyone who opens it can join as a Member, and you are shown as the person who ' +
            'invited them if they are new. An admin can turn the link off.',
        ),
      );
      out.hidden = false;
    } catch (err) {
      status.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
    } finally {
      get.removeAttribute('disabled');
    }
  });

  return el('div', { class: 'invite-link-block' }, [
    el(
      'p',
      { class: 'muted small' },
      'Or share a link in a group chat. People who tap it can join this group.',
    ),
    get,
    out,
    status,
  ]);
}

/**
 * The hand-off for a phone invite to someone new: the inviter sends it.
 *
 * We text nobody here (see the route). An `sms:` link opens the inviter's own
 * Messages app with the number and text filled in, which is the whole point:
 * the message comes from a number the recipient knows. The `?&body=` form is
 * the one both iOS and Android read; iOS ignores a bare `?body=`.
 *
 * The text and a Copy button are always shown too, because a computer has no
 * Messages app and the link there does nothing visible.
 */
function showLinkPanel(panel: HTMLElement, invite: { url: string; phone: string; smsBody: string }): void {
  const status = el('p', { class: 'muted small', role: 'status' }, '');
  const copy = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Copy message');
  copy.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(invite.smsBody);
      status.textContent = 'Copied. Paste it into a text to them.';
    } catch {
      status.textContent = 'Could not copy. Select the message above and copy it.';
    }
  });

  panel.replaceChildren(
    el('p', {}, [el('strong', {}, 'One more step: '), 'send this invite from your phone.']),
    el(
      'p',
      { class: 'muted small' },
      'They have no account yet, so MySocialCal does not text them. It comes from you, ' +
        'from a number they know.',
    ),
    el('textarea', { class: 'invite-link-text', readonly: true, rows: 3 }, invite.smsBody),
    el('div', { class: 'invite-link-actions' }, [
      el(
        'a',
        { class: 'btn', href: `sms:${invite.phone}?&body=${encodeURIComponent(invite.smsBody)}` },
        'Open Messages',
      ),
      copy,
    ]),
    status,
    el(
      'p',
      { class: 'muted small' },
      'The link works for 48 hours. Inviting the same number again gives you the same link.',
    ),
  );
  panel.hidden = false;

  // ⚠ Bring it ON SCREEN. The panel renders under the form, and on a phone the
  // Send button is usually at the bottom of the viewport, so the panel landed
  // entirely below the fold: the only visible change was the button greying
  // out, which read as a dead button (found on an iPhone, 2026-10-15). Measured
  // at 375x812: panel top 828, Open Messages 1107. Focus goes to the next action
  // without a second jump.
  panel.scrollIntoView({ block: 'start', behavior: 'smooth' });
  panel.querySelector<HTMLElement>('a.btn')?.focus({ preventScroll: true });
}

/**
 * What to tell the inviter.
 *
 * The `emailUnsubscribed` branch is the one worth reading. The person WAS
 * added -- an email opt-out is about mail, not membership -- but no notice went
 * out, so the only way they learn is if the inviter tells them. Saying so is
 * the entire point: it may well have been an accident, and the inviter is the
 * one person positioned to mention it.
 *
 * Note what it does NOT promise. Turning email back on needs the link in a past
 * MySocialCal email; there is no in-app switch for it (views/notifications.ts
 * tells the person themselves the same thing). Pointing the inviter at a
 * settings page would be advice that leads nowhere.
 */
function successMessage(result: InviteMemberResponse): string {
  if (result.mode === 'invited' || result.mode === 'link') {
    return `Invite sent. They will join as ${result.roleLabel} once they accept.`;
  }

  const added = `Already had an account, so they were added as ${result.roleLabel}.`;

  if (result.emailUnsubscribed) {
    return (
      `${added} They have unsubscribed from MySocialCal email, so we did not send them ` +
      `a notification, so let them know another way. If it was a mistake, they can ` +
      `turn email back on in their notification settings.`
    );
  }

  return result.notifyOk
    ? added
    : `${added} We could not send the courtesy email, but the add stands.`;
}
